import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { downloadStore, generalStore, userStore } from '../../store';
import { DownloadRecord, Ad } from '../../types';
import { BASE_URL, API } from '../../database/apis';
import { ArrowLeft, ChevronLeft, ChevronRight, AlertTriangle, ZoomIn, ZoomOut, Maximize2, Minimize2 } from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { showInterstitialFallback } from '../../lib/admob';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Bundle the pdfjs worker locally (we copied it to /public during setup).
// The previous CDN URL pointed at .min.js but pdfjs v5 (what react-pdf 10
// ships with) renamed the worker to .min.mjs, so the dynamic import was
// failing at runtime. Serving from /public also kills the unpkg dependency.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

// Utility: check file extension type
const checkExtensionType = (ext: string): 'image' | 'video' | '' => {
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'm4v'];
  if (imageExts.includes(ext.toLowerCase())) return 'image';
  if (videoExts.includes(ext.toLowerCase())) return 'video';
  return '';
};

const PdfViewerScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const download = (location.state as any)?.download as DownloadRecord | undefined;
  // When opened from the user's owned library (paid purchase), the route
  // state carries owned=true. Owned reads are ad-free, and the file URL
  // resolves to the /library/[id]/file endpoint instead of /downloads.
  const isOwned: boolean = !!(location.state as any)?.owned;

  const book = download?.adverbook;

  // PDF state
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [showGoToPage, setShowGoToPage] = useState(false);
  const [goToPageInput, setGoToPageInput] = useState('');

  // Reader UX state — zoom and fullscreen
  const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Distinguishes a quick double-tap (zoom toggle) from a single tap.
  const lastTapRef = useRef<number>(0);

  const zoomIn = () => {
    const next = ZOOM_LEVELS.find((z) => z > zoom);
    if (next != null) setZoom(next);
  };
  const zoomOut = () => {
    // Walk the array backwards for the largest level still below current zoom.
    for (let i = ZOOM_LEVELS.length - 1; i >= 0; i--) {
      if (ZOOM_LEVELS[i] < zoom) {
        setZoom(ZOOM_LEVELS[i]);
        return;
      }
    }
  };
  const handlePageDoubleTap = () => {
    setZoom((z) => (z >= 2 ? 1 : 2));
  };
  // We can't rely on dblclick alone on touch — simulate it with a 300ms gap.
  const handlePagePointerDown = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      handlePageDoubleTap();
      lastTapRef.current = 0;
      return;
    }
    lastTapRef.current = now;
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (e) {
      // Some webviews (older iOS WKWebView) don't support fullscreen API —
      // user will only get the chrome-hidden experience next time we add
      // auto-hide. For now silently ignore.
      console.log('Fullscreen toggle failed:', e);
    }
  };

  // Keep the icon in sync if the user hits Esc to leave fullscreen.
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Ad state
  const [isShowAd, setIsShowAd] = useState(false);
  const [adType, setAdType] = useState<'image' | 'video' | ''>('');
  const [adData, setAdData] = useState<any>(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const [adLoadError, setAdLoadError] = useState(false);
  const [adPageTracker, setAdPageTracker] = useState(0);
  const [adPageTrackerActive, setAdPageTrackerActive] = useState(false);
  const repeatTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Data from book & default ads
  const ads = book?.ads || [];
  const slots: string[] = book?.ad_slots || [];
  const defaultAdObj = downloadStore.defaultAd;
  const adTime = (defaultAdObj as any)?.ad_time || 20;
  const repeatTime = (defaultAdObj as any)?.repeat_time || 0;

  // D2P now SERVES the PDF directly via an authenticated streaming endpoint
  // (no presigned B2 URLs). For ad-supported reads we hit /downloads/[id]/file,
  // for owned reads /library/[id]/file. JWT goes in the URL since pdf.js's
  // worker can't set Authorization headers cleanly.
  const pdfUrl = useMemo(() => {
    // Owned reads need a real user JWT; ad-supported reads accept an
    // anonymous device JWT, so fall through to effectiveToken there.
    const token = isOwned ? userStore.authToken : userStore.effectiveToken;
    if (!download?._id || !token) return '';
    const path = isOwned
      ? API.OWNED_FILE(download._id)
      : API.DOWNLOAD_FILE(download._id);
    return `${BASE_URL}${path}?token=${encodeURIComponent(token)}`;
  }, [download?._id, isOwned]);

  // Restore saved page
  useEffect(() => {
    if (book && pdfLoaded) {
      const savedPage = downloadStore.getSavedPage(book._id);
      if (savedPage > 1 && savedPage <= totalPages) {
        setCurrentPage(savedPage);
      }
    }
  }, [book, pdfLoaded, totalPages]);

  // Save page on unmount
  useEffect(() => {
    return () => {
      if (book) {
        downloadStore.saveCurrentPage(book._id, currentPage);
      }
    };
  }, [book, currentPage]);

  // Handle window resize for responsive PDF
  useEffect(() => {
    const handleResize = () => setPageWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Slot-based ad system: check if current page is an ad slot.
  // Owned books skip this entirely — no ads for paid copies.
  // Phase 5: ask the server for the freshest ad on every slot trigger
  // (no client caching), then fall through to the per-book ads array
  // and finally the global default if the server returns null.
  useEffect(() => {
    if (isOwned) return;
    if (slots.length > 0 && !isShowAd && book?._id) {
      const pageStr = currentPage.toString();
      const slotIndex = slots.indexOf(pageStr);

      if (slotIndex > -1) {
        let cancelled = false;
        (async () => {
          let adObj: any = await downloadStore.serveAd(book._id);
          if (cancelled) return;

          if (!adObj && ads.length > 0 && ads[slotIndex]) adObj = ads[slotIndex];
          if (!adObj && defaultAdObj && (defaultAdObj as any).ad_file) adObj = defaultAdObj;
          if (!adObj) {
            // Empty proprietary inventory — fall through to AdMob if we
            // can. On web (or when fallback is disabled) this is a
            // no-op; the reader simply skips the slot.
            await showInterstitialFallback();
            return;
          }

          const adFile = adObj.ad_file || adObj.image || adObj.video || adObj.mediaUrl || '';
          const ext = (adFile || '').split('.').pop() || '';
          const type: 'image' | 'video' | '' =
            adObj.type === 'image' || adObj.type === 'video' ? adObj.type : checkExtensionType(ext);
          if (!type) return;

          setAdType(type);
          setAdData(adObj);
          setAdLoaded(false);
          setAdLoadError(false);
          setIsShowAd(true);
          setAdPageTracker(currentPage);
          setAdPageTrackerActive(true);
        })();
        return () => { cancelled = true; };
      }
    }
  }, [currentPage, slots, ads, defaultAdObj, isShowAd, isOwned, book?._id]);

  // Repeat timer
  useEffect(() => {
    if (isOwned) return;
    if (adPageTrackerActive && !isShowAd && repeatTime > 0) {
      if (adPageTracker !== currentPage) {
        setAdPageTrackerActive(false);
        setAdPageTracker(0);
        if (repeatTimerRef.current) clearInterval(repeatTimerRef.current);
        return;
      }

      repeatTimerRef.current = setInterval(() => {
        if (generalStore.isInternet) {
          setIsShowAd(true);
        }
      }, repeatTime * 1000);

      return () => {
        if (repeatTimerRef.current) clearInterval(repeatTimerRef.current);
      };
    }
  }, [adPageTrackerActive, isShowAd, currentPage, adPageTracker, repeatTime, isOwned]);

  // Track ad view + capture the server-issued ad-show token. The token
  // is stashed in the download store so the next forward progress PATCH
  // can attach it; without a valid token the server refuses to advance
  // the page past the slot, making the ad lock unbypassable from a
  // tampered client.
  useEffect(() => {
    if (isShowAd && adLoaded && adData && download?._id) {
      if (generalStore.isInternet) {
        const trackData = { ...adData };
        delete trackData.ad_time;
        delete trackData.repeat_time;
        downloadStore
          .trackAdView(trackData, download._id, currentPage)
          .then((tokenId) => {
            if (tokenId) downloadStore.setPendingAdToken(download._id, tokenId);
          });
      }
    }
  }, [isShowAd, adLoaded, adData, download, currentPage]);

  const handlePageChange = (direction: 'next' | 'prev') => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleGoToPage = () => {
    const page = parseInt(goToPageInput);
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      setShowGoToPage(false);
      setGoToPageInput('');
    }
  };

  const getAdFileUrl = (data: any): string => {
    return data?.ad_file || data?.image || data?.video || '';
  };

  // Every ad tap routes through the C5M tracking redirect at
  //   GET /api/d2p/kleverbook/ads/click/[adId]?downloadId=&page=
  // which writes a KleverBookAdClick row and 302s to the advertiser. We
  // open it in the Capacitor in-app browser so closing returns to this
  // exact reader page (the PdfViewer state is preserved). The destination
  // URL itself comes from the server, not the client — this prevents a
  // tampered client from bypassing tracking.
  const handleAdRedirect = async (_url?: string) => {
    if (!adLoaded) return;
    const adId: string | undefined = adData?._id || adData?.id;
    if (!adId) return;

    const params = new URLSearchParams();
    if (download?._id) params.set('downloadId', download._id);
    if (Number.isFinite(currentPage)) params.set('page', String(currentPage));
    const token = userStore.effectiveToken;
    if (token) params.set('token', token);
    const clickUrl = `${BASE_URL}${API.AD_CLICK(adId)}?${params.toString()}`;

    try {
      await Browser.open({ url: clickUrl, presentationStyle: 'popover' });
    } catch (e) {
      // Web fallback (dev / non-Capacitor): the redirect will still log
      // the click on the server, so attribution survives.
      window.open(clickUrl, '_blank');
    }
  };

  if (!book) {
    navigate('/app/library', { replace: true });
    return null;
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col bg-gray-900">
      {/* Header */}
      <div className="safe-area-top bg-gray-800/90 backdrop-blur-sm">
        <div className="flex items-center px-3 py-3 gap-2">
          <button
            onClick={() => {
              if (book) downloadStore.saveCurrentPage(book._id, currentPage);
              navigate(-1);
            }}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1 min-w-0 text-center">
            <h1 className="text-sm font-semibold text-white truncate">
              {book.title || book.book_title || 'Book'}
            </h1>
            <button
              onClick={() => setShowGoToPage(true)}
              className="text-xs text-white/60 active:text-white/80"
            >
              Page {currentPage} {totalPages > 0 ? `of ${totalPages}` : ''}
            </button>
          </div>
          {/* Zoom + fullscreen controls */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={zoomOut}
              disabled={zoom <= ZOOM_LEVELS[0]}
              aria-label="Zoom out"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 disabled:opacity-40"
            >
              <ZoomOut className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={() => setZoom(1)}
              aria-label={`Zoom level ${Math.round(zoom * 100)}%, tap to reset`}
              className="min-w-[44px] h-9 px-1 text-[11px] text-white/80 active:text-white rounded-full bg-white/10 active:bg-white/20"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={zoomIn}
              disabled={zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
              aria-label="Zoom in"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 disabled:opacity-40"
            >
              <ZoomIn className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 ml-0.5"
            >
              {isFullscreen ? (
                <Minimize2 className="w-4 h-4 text-white" />
              ) : (
                <Maximize2 className="w-4 h-4 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Internet Warning */}
      {!generalStore.isInternet && !isShowAd && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 top-[60px]">
          <p className="text-white text-sm px-6 text-center">
            Please connect to the internet to continue reading...
          </p>
        </div>
      )}

      {/* PDF Content */}
      <div
        className="flex-1 relative overflow-auto flex justify-center bg-gray-800"
        onPointerDown={handlePagePointerDown}
        onDoubleClick={handlePageDoubleTap}
      >
        {pdfUrl ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages }) => {
              setTotalPages(numPages);
              setPdfLoaded(true);
            }}
            onLoadError={(error) => {
              console.error('PDF load error:', error);
              setPdfError(true);
            }}
            loading={
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            }
          >
            <Page
              pageNumber={currentPage}
              width={pageWidth * zoom}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-white/60 text-sm">PDF file not available</p>
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mt-4 text-white"
              >
                Go Back
              </Button>
            </div>
          </div>
        )}

        {pdfError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="text-center">
              <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-white/60 text-sm">Failed to load PDF</p>
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mt-4 text-white"
              >
                Go Back
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Page Navigation */}
      <div className="bg-gray-800/90 backdrop-blur-sm safe-area-bottom">
        <div className="flex items-center justify-between px-6 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handlePageChange('prev')}
            disabled={currentPage <= 1}
            className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <button
            onClick={() => setShowGoToPage(true)}
            className="flex items-center gap-1 px-4 py-2 bg-white/10 rounded-lg active:bg-white/20"
          >
            <span className="text-white text-sm font-semibold">{currentPage}</span>
            <span className="text-white/60 text-sm">/ {totalPages || '...'}</span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handlePageChange('next')}
            disabled={currentPage >= totalPages}
            className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Go To Page Dialog */}
      <Dialog open={showGoToPage} onOpenChange={setShowGoToPage}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Go to page</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              value={goToPageInput}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                if (val === '' || (parseInt(val) > 0 && parseInt(val) <= totalPages)) {
                  setGoToPageInput(val);
                }
              }}
              placeholder="Page number"
              autoFocus
              className="flex-1"
            />
            <span className="text-sm text-muted-foreground whitespace-nowrap">of {totalPages}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGoToPage(false); setGoToPageInput(''); }}>
              Cancel
            </Button>
            <Button onClick={handleGoToPage} disabled={!goToPageInput} className="bg-secondary text-secondary-foreground">
              Go
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ad Overlay */}
      {isShowAd && adData && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
          <div className="safe-area-top" />

          {/* Ad Content */}
          <div className="flex-1 flex items-center justify-center p-3">
            <button
              className="w-full h-full flex items-center justify-center"
              onClick={() => handleAdRedirect()}
            >
              {adType === 'image' && (
                <>
                  <img
                    src={getAdFileUrl(adData)}
                    alt="Advertisement"
                    className="max-w-full max-h-full object-contain rounded-xl"
                    onLoad={() => setAdLoaded(true)}
                    onError={() => setAdLoadError(true)}
                  />
                  {!adLoaded && !adLoadError && (
                    <div className="absolute">
                      <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </>
              )}

              {adType === 'video' && (
                <>
                  <video
                    src={getAdFileUrl(adData)}
                    autoPlay
                    playsInline
                    // Anti-bypass: explicitly forbid PiP, AirPlay, download,
                    // remote-playback. autoPlay + no controls + onEnded
                    // stops users from skipping by seeking.
                    disablePictureInPicture
                    disableRemotePlayback
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                    className="max-w-full max-h-full rounded-xl"
                    style={{ maxHeight: 'calc(100vh - 200px)' }}
                    onLoadedData={() => setAdLoaded(true)}
                    onError={() => setAdLoadError(true)}
                    onEnded={() => setIsShowAd(false)}
                  />
                  {!adLoaded && !adLoadError && (
                    <div className="absolute">
                      <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </>
              )}
            </button>
          </div>

          {/* Ad Error */}
          {adLoadError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black/90 rounded-xl px-6 py-4 text-center">
                <p className="text-gray-400 text-sm mb-2">
                  {adType === 'video' ? 'Video' : 'Image'} ad load error!
                </p>
                <button
                  onClick={() => setIsShowAd(false)}
                  className="text-white text-sm underline"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Countdown Timer */}
          {adLoaded && adType === 'image' && (
            <div className="absolute top-3 right-3 safe-area-top bg-black rounded-full">
              <CountdownCircleTimer
                isPlaying
                duration={adTime}
                size={36}
                strokeWidth={2.5}
                colors={['#004777', '#F7B801', '#A30000', '#A30000']}
                colorsTime={[
                  adTime,
                  Math.round(adTime / 3),
                  Math.round(adTime / 2),
                  0,
                ]}
                onComplete={() => {
                  setIsShowAd(false);
                  return { shouldRepeat: false };
                }}
              >
                {({ remainingTime }: { remainingTime: number }) => (
                  <span className="text-white text-xs font-bold">{remainingTime}</span>
                )}
              </CountdownCircleTimer>
            </div>
          )}

          <div className="safe-area-bottom" />
        </div>
      )}
    </div>
  );
});

export default PdfViewerScreen;
